import { FC, useCallback, useRef } from "react";

import { BannerSource } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateDonationDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { LiveFormEditorV2StepBannerStateless, StepBannerFormData } from "./LiveFormEditorV2StepBanner.stateless";

import { trpc } from "@/common/helpers/trpc";
import { ImageUploaderHandle } from "@/components/ImageUploader/types";
import { BannerFormat } from "@/dashboard-design-system/RadioGroup";

export const DonationLiveEditorStepBanner: FC = () => {
  const {
    apiFormObject: apiDonationForm,
    updateApiFormObject,
    optionalFundraiserToken,
  } = useLiveFormEditorContext<"donation-form">();

  const { liveFormAmplitudePayload } = useLiveFormAmplitudeEvents();

  const bannerRef = useRef<ImageUploaderHandle>(null);
  const pendingBannerSourceRef = useRef<BannerSource | null | undefined>(undefined);

  const isLegacyBanner = !apiDonationForm.isSquareBanner;

  const defaultValues: StepBannerFormData = {
    bannerFormat: apiDonationForm.bannerVideoUrl ? BannerFormat.Video : BannerFormat.Image,
    banner: apiDonationForm.bannerUrl || null,
    bannerVideoUrl: apiDonationForm.bannerVideoUrl || undefined,
    isBannerUploading: false,
    isSquareBanner: apiDonationForm.isSquareBanner ?? false,
    bannerAltText: apiDonationForm.bannerAltText ?? null,
  };
  const form = useForm<StepBannerFormData>({
    defaultValues,
  });

  const { mutateAsync: updateBanner } = trpc.updateDonationFormBannerFields.useMutation();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepBannerFormData>({
    type: "donation-form",
    form,
    saveData: async (formData) => {
      // Finalize any pending square crop before saving
      const finalizedSquareCropUrl = await bannerRef.current?.finalizeSquareCrop();
      const bannerUrl = finalizedSquareCropUrl ?? formData.banner;
      const isBannerTouched = Boolean(finalizedSquareCropUrl) || form.getFieldState("banner").isDirty;

      // Only send bannerSource if user changed the image during this session
      const bannerSource = pendingBannerSourceRef.current;

      return updateBanner({
        donationFormId: apiDonationForm.id,
        ...(isBannerTouched && {
          bannerUrl,
          bannerAltText: bannerUrl ? (form.getValues("bannerAltText") ?? null) : null,
        }),
        ...(form.getFieldState("bannerVideoUrl").isDirty && { bannerVideoUrl: formData.bannerVideoUrl || null }),
        isSquareBanner: formData.isSquareBanner,
        ...(bannerSource !== undefined && { bannerSource }),
        fundraiserToken: optionalFundraiserToken,
      });
    },
  });

  const handleBannerSourceChange = useCallback((source: BannerSource | null) => {
    pendingBannerSourceRef.current = source;
  }, []);

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.donation.bannerUrl = formData.banner;
      liveDataToMutate.donation.bannerAltText = formData.banner ? (formData.bannerAltText ?? null) : null;
      liveDataToMutate.donation.bannerVideoUrl = formData.bannerVideoUrl || null;
      if (formData.isSquareBanner !== undefined) {
        liveDataToMutate.donation.isSquareBanner = formData.isSquareBanner;
      }
    },
  });

  const handleConfirmMigration = useCallback(() => {
    form.setValue("isSquareBanner", true, { shouldDirty: true });
    updateApiFormObject({ ...apiDonationForm, isSquareBanner: true });
  }, [form, apiDonationForm, updateApiFormObject]);

  const handleLegacyImageChange = useCallback((): Promise<boolean> => {
    // Apply migration to square format when user changes the legacy banner
    handleConfirmMigration();
    return Promise.resolve(true);
  }, [handleConfirmMigration]);

  return (
    <LiveFormEditorV2StepBannerStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      handleSaveAndBack={handleSaveAndBack}
      amplitudePayload={liveFormAmplitudePayload}
      bannerRef={bannerRef}
      isLegacyBanner={isLegacyBanner}
      onLegacyImageChange={handleLegacyImageChange}
      fundraiserContext={{ donationFormId: apiDonationForm.id, fundraiserToken: optionalFundraiserToken }}
      onBannerSourceChange={handleBannerSourceChange}
    />
  );
};
